import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faCalendarAlt,
    faLayerGroup,
    faRupeeSign,
    faPlus,
    faEdit,
    faSearch,
    faClock,
    faListOl,
} from "@fortawesome/free-solid-svg-icons";
import {
    Row,
    Col,
    Card,
    Table,
    InputGroup,
    Form,
    Button,
    Modal,
    Badge,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    adminGetCommissionConfigById,
    adminAllLevelCommission,
    adminAddLevelCommission,
    adminEditLevelCommission,
    adminGetLevelCommissionById,
} from "../../../pages/api";
import { Routes } from "../../../routes";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const monthOptions = [
    { value: "", label: "Select Month" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

const getMonthLabel = (month) => {
    const item = monthOptions.find((m) => String(m.value) === String(month));
    return item?.label || "N/A" || "-";
};

const formatAmount = (value) => {
    const num = Number(value || 0);
    return Number.isInteger(num) ? `${num}` : `${num.toFixed(2)}`;
};

const normalizeConfig = (data = {}) => ({
    id: data?.id ?? null,
    month: String(data?.month || ""),
    year: String(data?.year || ""),
    totalPoolAmount: String(
        data?.total_pool_amount ??
            data?.totalPoolAmount ??
            data?.pool_amount ??
            ""
    ),
});

const normalizeLevel = (item = {}) => ({
    id: item?.id ?? Date.now(),
    config_id: item?.config_id ?? null,
    level: String(item?.level ?? ""),
    amount: String(item?.amount ?? item?.percentage ?? ""),
});

const createEmptyForm = (configId = "") => ({
    config_id: String(configId || ""),
    level: "",
    amount: "",
});

const getStatusBadge = (value) => {
    const amount = Number(value || 0);
    if (amount <= 0) return "secondary";
    if (amount < 100) return "info";
    return "success";
};

export default function AdminLevelCommissions() {
    const navigate = useNavigate();
    const { configId } = useParams();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [config, setConfig] = useState(null);
    const [levels, setLevels] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingLevel, setEditingLevel] = useState(null);
    const [formData, setFormData] = useState(createEmptyForm(configId));
    const [errors, setErrors] = useState({});

    const searchDebounceRef = useRef(null);
    const firstSearchRenderRef = useRef(true);

    const totalPoolAmount = Number(config?.totalPoolAmount || 0);

    const allocatedAmount = useMemo(() => {
        return levels.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    }, [levels]);

    const remainingAmount = useMemo(() => {
        return totalPoolAmount - allocatedAmount;
    }, [totalPoolAmount, allocatedAmount]);

    const cycleLabel = useMemo(() => {
        if (!config?.month || !config?.year) return "N/A" || "-";
        return `${getMonthLabel(config.month)} ${config.year}`;
    }, [config]);

    const filteredLevels = useMemo(() => {
        return levels;
    }, [levels]);

    const availableLevels = useMemo(() => {
        const used = new Set(levels.map((item) => String(item.level)));
        return Array.from({ length: 9 }, (_, index) => String(index + 1)).filter(
            (lvl) => {
                if (editingLevel && String(editingLevel.level) === String(lvl)) return true;
                return !used.has(String(lvl));
            }
        );
    }, [levels, editingLevel]);

    const extractApiData = (response) => response?.data?.data ?? response?.data ?? {};

    const extractLevelList = (response) => {
        const data = response?.data?.data ?? response?.data ?? [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.rows)) return data.rows;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    };

    const fetchData = async (searchTerm = "") => {
        try {
            setLoading(true);

            const configResponse = await adminGetCommissionConfigById(configId);
            const configData = extractApiData(configResponse);

            setConfig(normalizeConfig(configData));

            try {
                const levelsResponse = await adminAllLevelCommission(configId, {
                    search: searchTerm,
                });

                const levelData = extractLevelList(levelsResponse);

                setLevels(
                    levelData
                        .map(normalizeLevel)
                        .sort((a, b) => Number(a.level) - Number(b.level))
                );
            } catch (levelError) {
                console.error("Failed to load level commissions:", levelError);
                setLevels([]);
            }
        } catch (error) {
            console.error("Failed to load commission config:", error);
            setConfig(normalizeConfig({ id: configId }));
            setLevels([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!configId) {
            toast.error("Commission config id is missing.");
            navigate(-1);
            return;
        }

        fetchData("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configId]);

    useEffect(() => {
        if (firstSearchRenderRef.current) {
            firstSearchRenderRef.current = false;
            return;
        }

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            fetchData(search.trim());
        }, 300);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const openAddModal = () => {
        setEditingLevel(null);
        setErrors({});
        setFormData(createEmptyForm(configId));
        setShowModal(true);
    };

    const openEditModal = async (item) => {
        try {
            setSaving(true);
            const response = await adminGetLevelCommissionById(item.id);
            const data = response?.data?.data || response?.data || item;

            setEditingLevel(normalizeLevel(data));
            setFormData({
                config_id: String(data?.config_id || configId || ""),
                level: String(data?.level || ""),
                amount: String(data?.amount ?? data?.percentage ?? ""),
            });
            setErrors({});
            setShowModal(true);
        } catch (error) {
            console.error("Failed to load level:", error);
            toast.error(error?.response?.data?.message || "Unable to open level details.");
        } finally {
            setSaving(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingLevel(null);
        setFormData(createEmptyForm(configId));
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "level") {
            if (value !== "" && !/^\d*$/.test(value)) return;
            const nextLevel = Number(value || 0);
            if (nextLevel < 1 || nextLevel > 9) {
                setErrors((prev) => ({
                    ...prev,
                    level: "Level must be between 1 and 9.",
                }));
            } else {
                setErrors((prev) => ({ ...prev, level: "" }));
            }
        }

        if (name === "amount") {
            if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
            form: "",
        }));
    };

    const validate = () => {
        const nextErrors = {};
        const levelValue = Number(formData.level);
        const amountValue = Number(formData.amount);

        if (!formData.config_id) {
            nextErrors.form = "Commission config id is missing.";
        }

        if (!formData.level) {
            nextErrors.level = "Level is required.";
        } else if (levelValue < 1 || levelValue > 9) {
            nextErrors.level = "Level must be between 1 and 9.";
        }

        if (!formData.amount || amountValue <= 0) {
            nextErrors.amount = "Amount is required.";
        }

        const duplicateLevel = levels.find(
            (item) =>
                Number(item.level) === levelValue &&
                String(item.id) !== String(editingLevel?.id || "")
        );

        if (duplicateLevel) {
            nextErrors.level = "This level already exists for this config.";
        }

        const currentAllocated = levels.reduce((sum, item) => {
            if (String(item.id) === String(editingLevel?.id || "")) {
                return sum;
            }
            return sum + Number(item.amount || 0);
        }, 0);

        const nextTotal = currentAllocated + amountValue;

        if (totalPoolAmount > 0 && nextTotal > totalPoolAmount) {
            nextErrors.amount = `Total allocation cannot exceed ${totalPoolAmount}.`;
            nextErrors.form = `Configured share total would exceed the commission pool amount.`;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error(errors.form || "Please correct the highlighted fields.");
            return;
        }

        try {
            setSaving(true);

            const payload = {
                config_id: Number(formData.config_id),
                level: Number(formData.level),
                amount: Number(formData.amount),
            };

            if (editingLevel) {
                const response = await adminEditLevelCommission(editingLevel.id, payload);
                toast.success(
                    response?.data?.message || "Level commission updated successfully."
                );
            } else {
                const response = await adminAddLevelCommission(payload);
                toast.success(
                    response?.data?.message || "Level commission added successfully."
                );
            }

            closeModal();
            await fetchData(search.trim());
        } catch (error) {
            console.error("Level commission save failed:", error);
            toast.error(
                error?.response?.data?.message || "Unable to save level commission."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>

            <div className="commission-header py-4">
                <div className="commission-header-title">
                    <h5 className="mb-1">Admin Level Commissions</h5>
                    <div className="text-muted small">
                        Commission Slab Management for {cycleLabel}
                    </div>
                </div>

                <div className="commission-header-actions">
                    <Button
                        type="button"
                        onClick={openAddModal}
                        className="commission-add-btn fw-semibold"
                        style={ACTIVE_BUTTON_STYLE}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Add Level
                    </Button>

                    <Button
                        type="button"
                        variant="outline-primary"
                        className="commission-back-btn fw-semibold"
                        as={Link}
                        to={Routes.AdminCommissionList.path}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                </div>
            </div>

            <Row className="g-4 mb-4">
                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="text-muted small mb-1">Active Cycle</div>
                                    <h3 className="mb-0">{cycleLabel}</h3>
                                </div>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        background: "rgba(44, 96, 186, 0.1)",
                                        color: "#005dfb",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="text-muted small mb-1">Commission Pool Amount</div>
                                    <h3 className="mb-0">{formatAmount(totalPoolAmount)}</h3>
                                </div>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        background: "rgba(44, 96, 186, 0.1)",
                                        color: "#005dfb",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRupeeSign} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="text-muted small mb-1">Configured Amount</div>
                                    <h3 className="mb-0">{formatAmount(allocatedAmount)}</h3>
                                </div>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        background: "rgba(44, 96, 186, 0.1)",
                                        color: "#005dfb",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} xl={3}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="text-muted small mb-1">Remaining Amount</div>
                                    <h3 className="mb-0">{formatAmount(remainingAmount)}</h3>
                                </div>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        background: "rgba(44, 96, 186, 0.1)",
                                        color: "#005dfb",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faClock} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm rounded-4">
                <Card.Body className="p-4">
                    <div className="commission-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div>
                            <h5 className="mb-1">Level List</h5>
                            {/* <div className="text-muted small">
                                Maximum 20 levels. Total allocation cannot exceed commission pool amount.
                            </div> */}
                        </div>

                        <InputGroup style={{ maxWidth: "320px" }}>
                            <InputGroup.Text className="bg-white">
                                <FontAwesomeIcon icon={faSearch} className="text-muted" />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ boxShadow: "none" }}
                            />
                        </InputGroup>
                    </div>

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Level</th>
                                    <th>Amount</th>
                                    {/* <th>Status</th> */}
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted py-5">
                                            Loading level commissions...
                                        </td>
                                    </tr>
                                ) : filteredLevels.length > 0 ? (
                                    filteredLevels.map((item) => (
                                        <tr key={item.id}>
                                            <td className="fw-semibold">
                                                {/* <FontAwesomeIcon icon={faListOl} className="me-2 text-muted" /> */}
                                                Level {item.level}
                                            </td>
                                            <td>{formatAmount(item.amount)}</td>
                                            {/* <td>
                        <Badge bg={getStatusBadge(item.amount)} className="px-3 py-2 rounded-pill">
                          {Number(item.amount || 0) > 0 ? "Active" : "Inactive"}
                        </Badge>
                      </td> */}
                                            <td className="text-center">
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="p-0 text-decoration-none"
                                                    onClick={() => openEditModal(item)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} size="sm" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted py-5">
                                            No level commissions configured yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={closeModal} centered backdrop="static">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {editingLevel ? "Edit Level Commission" : "Add Level Commission"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Row className="g-3">
                            {/* <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Config ID <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="config_id"
                    type="text"
                    value={formData.config_id}
                    readOnly
                    disabled
                    style={{ boxShadow: "none" }}
                  />
                </Form.Group>
              </Col> */}

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">
                                        Level <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="level"
                                        value={formData.level}
                                        onChange={handleChange}
                                        isInvalid={!!errors.level}
                                        style={{ boxShadow: "none" }}
                                    >
                                        <option value="">Select Level</option>
                                        {availableLevels.map((level) => (
                                            <option key={level} value={level}>
                                                Level {level}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.level}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">
                                        Amount <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        name="amount"
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="Enter amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        isInvalid={!!errors.amount}
                                        style={{ boxShadow: "none" }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.amount}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <div className="p-3 rounded-4 bg-light">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Commission Pool Amount</span>
                                        <span className="fw-semibold">{formatAmount(totalPoolAmount)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Allocated Amount</span>
                                        <span className="fw-semibold">{formatAmount(allocatedAmount)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Remaining Amount</span>
                                        <span className="fw-semibold">{formatAmount(remainingAmount)}</span>
                                    </div>
                                </div>
                            </Col>

                            {errors.form ? (
                                <Col md={12}>
                                    <div className="alert alert-danger border-0 mb-0">{errors.form}</div>
                                </Col>
                            ) : null}
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={closeModal} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={saving}>
                            {saving ? "Saving..." : editingLevel ? "Update Level" : "Add Level"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style>{`
  .commission-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0;
  }

  .commission-header-title {
    flex: 0 0 auto;
  }

  .commission-header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex: 1;
  }

  .commission-add-btn {
    white-space: nowrap;
    flex: 0 1 auto;
    padding: 10px 20px;
  }

  .commission-back-btn {
    flex: 0 0 48px;
    width: 48px;
    min-width: 48px;
    height: 44px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .modal-content {
    border-radius: 18px;
  }

  @media (max-width: 767.98px) {
    .commission-header {
      flex-direction: column;
      align-items: stretch;
    }

    .commission-header-title {
      width: 100%;
    }

    .commission-header-actions {
      width: 100%;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      gap: 8px;
    }

    .commission-add-btn {
      flex: 1 1 auto;
      min-width: 0;
      width: auto;
    }

    .commission-back-btn {
      flex: 0 0 48px;
      width: 48px;
      min-width: 48px;
    }

    .commission-list-top {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .commission-list-top > div:first-child {
      width: 100%;
    }

    .commission-list-top .input-group {
      width: 100% !important;
      max-width: none !important;
    }
  }
`}</style>
        </>
    );
}